/**
 * Manual promote / ungroup on the reference list: make one selected reference
 * the parent and the rest its variants, or detach variants from their parent.
 *
 * Only `parentReferenceId` / `isVariant` move (plus the parent's
 * `variantReferences` search list); no doc is created, deleted or re-keyed, so
 * everything keyed by a reference id — user watches, images, calibre links —
 * stays linked. Watchlock reacts on its own: syncReferenceToAlgolia drops a
 * new variant from the index and re-adds a detached one, its isVariant
 * self-heal backs up the flag, and propagateVariantAggregatesToParent rolls
 * the new variants' values up into the parent.
 *
 * Depth stays one, so grouping is blocked (never half-applied) when the parent
 * is itself a variant, a member already belongs to another parent, or a
 * member has variants of its own.
 */
import { arrayRemove, arrayUnion, deleteField, doc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { deriveIsVariant } from '../utils/references';
import type { WatchReference } from '../types';

type RefLike = Pick<WatchReference, 'id' | 'reference'> & { parentReferenceId?: string | null };

export interface GroupConflict {
  refId: string;
  reference: string;
  reason: string;
}

export interface GroupPlan {
  parentId: string;
  /** Members that will become variants of the parent. */
  moveIds: string[];
  /** Members already variants of this parent — nothing to write. */
  alreadyGrouped: string[];
  /** Blocking problems; the plan must not be applied while any exist. */
  conflicts: GroupConflict[];
}

/**
 * Plan "Group as variants of a parent" over a selection.
 * `variantCounts` holds each selected ref's own variant count.
 */
export function planGroupAsVariants(
  selected: readonly RefLike[],
  parentId: string,
  variantCounts: ReadonlyMap<string, number>,
): GroupPlan {
  const conflicts: GroupConflict[] = [];
  const moveIds: string[] = [];
  const alreadyGrouped: string[] = [];
  const parent = selected.find((r) => r.id === parentId);
  if (!parent) {
    return { parentId, moveIds, alreadyGrouped, conflicts: [{ refId: parentId, reference: parentId, reason: 'The parent is not in the selection.' }] };
  }
  if (deriveIsVariant(parent.parentReferenceId)) {
    conflicts.push({ refId: parent.id, reference: parent.reference, reason: 'is itself a variant of another parent — ungroup it first.' });
  }
  for (const r of selected) {
    if (r.id === parentId) continue;
    if (r.parentReferenceId === parentId) {
      alreadyGrouped.push(r.id);
      continue;
    }
    if (deriveIsVariant(r.parentReferenceId)) {
      conflicts.push({ refId: r.id, reference: r.reference, reason: 'already belongs to another parent — ungroup it first.' });
      continue;
    }
    const own = variantCounts.get(r.id) ?? 0;
    if (own > 0) {
      conflicts.push({
        refId: r.id,
        reference: r.reference,
        reason: `has ${own} variant${own === 1 ? '' : 's'} of its own — variants can't nest. Ungroup them first.`,
      });
      continue;
    }
    moveIds.push(r.id);
  }
  return { parentId, moveIds, alreadyGrouped, conflicts };
}

const BATCH = 400;

/** Apply a conflict-free plan. Returns how many refs became variants. */
export async function applyGroupAsVariants(
  brandId: string,
  plan: GroupPlan,
  selected: readonly RefLike[],
): Promise<number> {
  if (plan.conflicts.length > 0) throw new Error('Resolve the conflicts before grouping.');
  if (plan.moveIds.length === 0) return 0;
  const byId = new Map(selected.map((r) => [r.id, r]));
  const now = Timestamp.now();
  const refPath = (id: string) => doc(db, 'watchBrands', brandId, 'references', id);
  for (let i = 0; i < plan.moveIds.length; i += BATCH) {
    const batch = writeBatch(db);
    for (const id of plan.moveIds.slice(i, i + BATCH)) {
      batch.update(refPath(id), { parentReferenceId: plan.parentId, isVariant: true, updatedAt: now });
    }
    await batch.commit();
  }
  // The parent's search list of variant SKUs (Algolia indexes the parent only).
  const skus = plan.moveIds.map((id) => byId.get(id)?.reference?.trim()).filter((s): s is string => !!s);
  if (skus.length > 0) {
    const batch = writeBatch(db);
    batch.update(refPath(plan.parentId), { variantReferences: arrayUnion(...skus), updatedAt: now });
    await batch.commit();
  }
  return plan.moveIds.length;
}

/**
 * Detach variants from their parents. Clears `parentReferenceId` (absent, the
 * way top-level refs are stored) and sets `isVariant: false`, then takes each
 * SKU off its old parent's `variantReferences`. Never deletes the parent — a
 * store-synthetic parent left with no variants stays until someone deletes it.
 */
export async function ungroupVariants(brandId: string, variants: readonly RefLike[]): Promise<number> {
  const targets = variants.filter((v) => deriveIsVariant(v.parentReferenceId));
  if (targets.length === 0) return 0;
  const now = Timestamp.now();
  const refPath = (id: string) => doc(db, 'watchBrands', brandId, 'references', id);
  for (let i = 0; i < targets.length; i += BATCH) {
    const batch = writeBatch(db);
    for (const v of targets.slice(i, i + BATCH)) {
      batch.update(refPath(v.id), { parentReferenceId: deleteField(), isVariant: false, updatedAt: now });
    }
    await batch.commit();
  }
  const byParent = new Map<string, string[]>();
  for (const v of targets) {
    const sku = v.reference?.trim();
    if (sku) byParent.set(v.parentReferenceId as string, [...(byParent.get(v.parentReferenceId as string) ?? []), sku]);
  }
  for (const [parentId, skus] of byParent) {
    const batch = writeBatch(db);
    batch.update(refPath(parentId), { variantReferences: arrayRemove(...skus), updatedAt: now });
    try {
      await batch.commit();
    } catch (err) {
      // The parent may already be gone; the variants are detached either way.
      console.warn(`Could not trim variantReferences on ${parentId}`, err);
    }
  }
  return targets.length;
}

/** A store import re-links a store variant to its store parent (fill-empty merge). */
export function isStoreVariant(ref: Pick<WatchReference, 'id'>): boolean {
  return /^(shopify|square|woocommerce)__/.test(ref.id) && !ref.id.endsWith('__parent');
}

/** Default parent for a selection: the shortest reference, then alphabetical. */
export function defaultGroupParent(selected: readonly RefLike[]): string | undefined {
  return [...selected].sort((a, b) => {
    const ra = (a.reference ?? '').trim();
    const rb = (b.reference ?? '').trim();
    return ra.length - rb.length || ra.localeCompare(rb);
  })[0]?.id;
}
